import { Component } from '@theme/component';

/**
 * On-brand cookie consent banner ("Davitta Cookie Consent.dc.html").
 *
 * All compliance state — whether a banner is owed at all (region/GDPR), the
 * saved decision, and the event other scripts listen to — lives in Shopify's
 * Customer Privacy API. This component is only the view on top of it: it
 * never sets its own cookie and never decides on its own whether consent is
 * required.
 *
 * Shopify's four consent categories (`analytics`, `marketing`, `preferences`,
 * `sale_of_data`) don't map 1:1 to the design's two toggles, so `marketing`
 * also carries `preferences`/`sale_of_data` — everything non-essential that
 * isn't analytics is one switch, matching the design.
 *
 * @typedef {Object} CookieBannerRefs
 * @property {HTMLElement} [dim] - Optional dark overlay behind the banner/prefs.
 * @property {HTMLElement} [banner] - The bar or card view.
 * @property {HTMLElement} [prefs] - The preferences panel.
 * @property {HTMLElement} [toast] - The post-decision confirmation.
 * @property {HTMLElement} [toastText] - Toast copy, swapped per action.
 * @property {HTMLButtonElement} [analyticsToggle] - `role="switch"` for analytics.
 * @property {HTMLButtonElement} [marketingToggle] - `role="switch"` for marketing.
 *
 * @extends {Component<CookieBannerRefs>}
 */
class DavittaCookieBanner extends Component {
  /** How long the post-decision toast stays visible before it auto-dismisses. */
  static #TOAST_DURATION_MS = 3000;

  /**
   * The categories a decision can be recorded against.
   *
   * @type {ReadonlyArray<keyof VisitorConsent>}
   */
  static #CONSENT_CATEGORIES = ['analytics', 'marketing', 'preferences', 'sale_of_data'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  /**
   * Whether the banner is still owed — the region asks for consent and the
   * visitor has not answered yet. Also gates where 'closePrefs' returns to.
   */
  #bannerOwed = false;

  /** Pending toggle state while the preferences panel is open. */
  #pending = { analytics: false, marketing: false };

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #toastTimeoutId;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();

    document.addEventListener('davitta:cookie-preferences:open', this.openPrefs, {
      signal: this.#abortController.signal,
    });

    // The toast carries a "Change" button, so its auto-dismiss must never run
    // out from under someone reaching for it — pause the clock while the
    // visitor is hovering or focused inside, and only resume once they leave.
    const { toast } = this.refs;
    if (toast) {
      const signal = this.#abortController.signal;
      toast.addEventListener('pointerenter', this.#pauseToastDismiss, { signal });
      toast.addEventListener('focusin', this.#pauseToastDismiss, { signal });
      toast.addEventListener('pointerleave', this.#resumeToastDismiss, { signal });
      toast.addEventListener('focusout', this.#resumeToastDismiss, { signal });
    }

    this.#setView('none');

    const shopify = window.Shopify;
    if (!shopify?.loadFeatures) return;

    shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (error) {
        console.warn('[davitta-cookie-consent] Could not load the consent tracking API:', error);
        return;
      }

      // Two independent questions, and 'shouldShowBanner' only answers the
      // first. Shopify's docs are explicit that it "doesn't indicate if consent
      // has already been given" — it is purely a region check, so for an EU
      // visitor it keeps returning true forever, decision or not. Reading it as
      // "a banner is due" re-prompts every Romanian shopper on every page no
      // matter what they picked; the answer itself lives in the consent record.
      this.#bannerOwed = (shopify.customerPrivacy?.shouldShowBanner?.() ?? false) && !this.#hasDecision;

      if (this.#bannerOwed) this.#setView('banner');
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    clearTimeout(this.#toastTimeoutId);
  }

  get #privacy() {
    return window.Shopify?.customerPrivacy;
  }

  /**
   * Whether a decision is already on record for this visitor.
   *
   * Categories read 'yes' / 'no' / '' (not answered). Shopify only records the
   * ones that apply to the visitor's region, so *any* non-empty value means the
   * visitor answered. Demanding all four instead would re-prompt anyone outside
   * a 'sale_of_data' region forever — the same failure this getter exists to fix.
   */
  get #hasDecision() {
    const consent = this.#privacy?.currentVisitorConsent?.();
    if (!consent) return false;

    return DavittaCookieBanner.#CONSENT_CATEGORIES.some((category) => consent[category] !== '');
  }

  openPrefs = () => {
    const consent = this.#privacy?.currentVisitorConsent?.();
    this.#pending = {
      analytics: consent?.analytics === 'yes',
      marketing: consent?.marketing === 'yes',
    };
    this.#syncToggles();
    this.#setView('prefs');
  };

  closePrefs = () => {
    this.#setView(this.#bannerOwed ? 'banner' : 'none');
  };

  toggleAnalytics = () => {
    this.#pending.analytics = !this.#pending.analytics;
    this.#syncToggles();
  };

  toggleMarketing = () => {
    this.#pending.marketing = !this.#pending.marketing;
    this.#syncToggles();
  };

  doAccept = () => {
    this.#saveConsent({ analytics: true, marketing: true }, this.dataset.toastAccepted);
  };

  doReject = () => {
    this.#saveConsent({ analytics: false, marketing: false }, this.dataset.toastRejected);
  };

  doSave = () => {
    this.#saveConsent(this.#pending, this.dataset.toastSaved);
  };

  /**
   * @param {{analytics: boolean, marketing: boolean}} choice
   * @param {string | undefined} toastText
   */
  #saveConsent(choice, toastText) {
    const privacy = this.#privacy;
    if (!privacy?.setTrackingConsent) return;

    privacy.setTrackingConsent(
      {
        analytics: choice.analytics,
        marketing: choice.marketing,
        preferences: choice.marketing,
        sale_of_data: choice.marketing,
      },
      () => {
        this.#bannerOwed = false;
        if (this.refs.toastText) this.refs.toastText.textContent = toastText ?? '';
        this.#setView('toast');
      }
    );
  }

  #syncToggles() {
    this.#applyToggle(this.refs.analyticsToggle, this.#pending.analytics);
    this.#applyToggle(this.refs.marketingToggle, this.#pending.marketing);
  }

  /**
   * @param {HTMLButtonElement | undefined} toggle
   * @param {boolean} on
   */
  #applyToggle(toggle, on) {
    if (!toggle) return;
    toggle.setAttribute('aria-checked', String(on));
    toggle.classList.toggle('is-on', on);
  }

  /** Start (or restart) the toast's auto-dismiss countdown. */
  #armToastDismiss = () => {
    clearTimeout(this.#toastTimeoutId);
    this.#toastTimeoutId = setTimeout(() => this.#setView('none'), DavittaCookieBanner.#TOAST_DURATION_MS);
  };

  #pauseToastDismiss = () => {
    clearTimeout(this.#toastTimeoutId);
  };

  /** @param {MouseEvent | FocusEvent} event */
  #resumeToastDismiss = (event) => {
    const { toast } = this.refs;
    if (!toast || toast.hidden) return;

    // Focus shifting to another element inside the toast is not a departure.
    const next = event.relatedTarget;
    if (next instanceof Node && toast.contains(next)) return;

    // Hover and focus pause independently; don't restart the clock while the
    // other one is still holding the toast open.
    if (toast.matches(':hover') || toast.contains(document.activeElement)) return;

    this.#armToastDismiss();
  };

  /** @param {'none' | 'banner' | 'prefs' | 'toast'} view */
  #setView(view) {
    const { banner, prefs, toast, dim } = this.refs;

    clearTimeout(this.#toastTimeoutId);

    if (banner) banner.hidden = view !== 'banner';
    if (prefs) prefs.hidden = view !== 'prefs';
    if (toast) toast.hidden = view !== 'toast';
    if (dim) dim.hidden = this.dataset.dimPage !== 'true' || (view !== 'banner' && view !== 'prefs');

    // The toast is a transient confirmation, not a persistent view — dismiss
    // it on its own after a few seconds unless the visitor has already
    // navigated away from it (e.g. via the "Change" link, which switches to
    // 'prefs' and clears this timeout above before it fires).
    if (view === 'toast') this.#armToastDismiss();
  }
}

if (!customElements.get('davitta-cookie-banner')) {
  customElements.define('davitta-cookie-banner', DavittaCookieBanner);
}
